<script lang="ts">
    import axios from 'axios';
    import Shgrid from '@/components/shgrid/shgrid.svelte';
    import type { Column } from '@/components/shgrid';
    import type { Lead } from '@/types/types';
    import * as Table from '@/components/ui/table/index.js';
    import PicklistBadge from '@/components/ui/picklist-badge/PicklistBadge.svelte';

    type StatusOption = {
        key: string;
        label: string;
        color: string;
        cssClass: string;
    };

    type Props = {
        onRowClick: (row: Lead) => void;
        statusOptions: StatusOption[];
    };

    let { onRowClick, statusOptions }: Props = $props();

    // Format currency for display
    function formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }

    let cols = $state<Column<Lead>[]>([
        {
            label: 'Ref',
            key: 'reference_number',
            cellRender: {
                type: 'text',
                label: (row) => row.reference ?? '',
            },
            sortable: true,
            filter: { type: 'string' },
        },
        {
            label: 'Name',
            key: 'name',
            cellRender: {
                type: 'text',
                label: (row) => row.name,
            },
            sortable: true,
            filter: { type: 'string' },
        },
        {
            label: 'Status',
            key: 'status',
            cellRender: {
                type: 'custom',
                snippet: statusBadgeSnippet,
            },
            sortable: true,
            filter: {
                type: 'select',
                options: statusOptions.map(opt => ({ value: opt.key, label: opt.label })),
            },
        },
        {
            label: 'Phone',
            key: 'phone',
            cellRender: {
                type: 'text',
                label: (row) => row.phone ?? "",
            },
            sortable: true,
            filter: { type: 'string' },
        },
        {
            label: 'Email',
            key: 'email',
            cellRender: {
                type: 'text',
                label: (row) => row.email ?? "",
            },
            sortable: true,
            filter: { type: 'string' },
        },
        {
            label: 'Organisation',
            key: 'organisation_name',
            cellRender: {
                type: 'text',
                label: (row) => row.organisation_name ?? "",
            },
            sortable: true,
            filter: { type: 'string' },
        },
        {
            label: 'Estimated Value',
            key: 'estimated_value',
            cellRender: {
                type: 'text',
                label: (row) => row.estimated_value ? formatCurrency(Number(row.estimated_value)) : "",
            },
            sortable: true,
            filter: { type: 'number' },
        },
        {
            label: 'Job Start',
            key: 'estimated_job_start',
            cellRender: {
                type: 'date',
                label: (row) => row.estimated_job_start,
            },
            sortable: true,
            filter: { type: 'date' },
        },
        {
            label: 'Created At',
            key: 'created_at',
            cellRender: {
                type: 'date',
                label: (row) => row.created_at,
            },
            sortable: true,
            filter: { type: 'date' },
        },
        {
            label: 'Last Updated',
            key: 'updated_at',
            cellRender: {
                type: 'date',
                label: (row) => row.updated_at,
            },
            sortable: true,
            filter: { type: 'date' },
        }
    ]);
</script>

{#snippet statusBadgeSnippet(row: Lead)}
    <Table.Cell>
        {#if row.status_label}
            <PicklistBadge label={row.status_label} color={row.status_color ?? 'gray'} />
        {:else}
            <span class="text-muted-foreground">{row.status ?? 'N/A'}</span>
        {/if}
    </Table.Cell>
{/snippet}

<div class="table-view-wrapper">
    <Shgrid
        fetcher={async ({ limit, offset, filters, sorter }) => {
            const res = await axios.get(route('web-api.Lead.index', { limit, offset, filters, sorter }));
            return { data: res.data, error: null };
        }}
        limit={50}
        bind:columns={cols}
        {onRowClick}
    />
</div>

<style>
    .table-view-wrapper {
        padding: 1rem;
    }
</style>